import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useHttp from "../../hooks/use-http";
import { endPoints } from "../../config/end-points";
import { submissionActions } from "../../store/submission-store";
import { projectActions } from "../../store/project-store";
import { authActions } from "../../store/auth";
import cuteAlert, { cuteToast } from "../UI/cute";
import { getSubdomainUrl } from "../../config";
import SubmissionDetail from "./SubmissionDetail";
import "./submissions.css";

const SubmissionsList = () => {
  const dispatch = useDispatch();
  const { sendRequest } = useHttp();
  const { submissions, filter } = useSelector((state) => state.submission);
  const { projects } = useSelector((state) => state.project);

  const [selectedId, setSelectedId] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    document.title = `${endPoints.title} | Form Submissions`;
    dispatch(authActions.setLoaderStatus({ status: false }));
    fetchSubmissions();
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.project_id, filter.status]);

  const fetchProjects = () => {
    sendRequest(
      { url: endPoints.projectsList + "/", method: "GET" },
      (response) => {
        if (response.status === 200) {
          dispatch(
            projectActions.setProjects({
              projects: response.content || [],
              totalPages: 0,
              currentPage: 0,
            })
          );
        }
      }
    );
  };

  const fetchSubmissions = () => {
    let url = endPoints.formSubmissions;
    const params = [];
    if (filter.project_id) params.push(`project_id=${filter.project_id}`);
    if (filter.status) params.push(`status=${filter.status}`);
    if (params.length > 0) url += "?" + params.join("&");

    sendRequest({ url, method: "GET" }, (response) => {
      if (response.status === 200) {
        dispatch(submissionActions.setSubmissions(response.data || []));
      }
    });
  };

  const handleViewSubmission = (id) => {
    setSelectedId(id);
    setShowDetail(true);
  };

  const handleStatusChange = (id, status) => {
    sendRequest(
      {
        url: `${endPoints.formSubmissions}/${id}/status`,
        method: "PUT",
        body: { status },
      },
      (response) => {
        if (response.status === 200) {
          dispatch(submissionActions.updateSubmissionStatus({ id, status }));
          cuteToast({ type: "success", title: "Status updated", message: `Submission marked as ${status}` });
        }
      }
    );
  };

  const handleDelete = (id) => {
    cuteAlert({
      type: "question",
      title: "Delete Submission?",
      message: "This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
    }).then((result) => {
      if (result === "confirm") {
        sendRequest(
          {
            url: `${endPoints.formSubmissions}/${id}`,
            method: "DELETE",
          },
          (response) => {
            if (response.status === 200) {
              dispatch(submissionActions.removeSubmission(id));
              cuteToast({ type: "success", title: "Submission deleted" });
              if (selectedId === id) {
                setShowDetail(false);
                setSelectedId(null);
              }
            }
          }
        );
      }
    });
  };

  const handleFilterChange = (key, value) => {
    dispatch(submissionActions.setFilter({ [key]: value }));
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const parseSubmissionData = (dataStr) => {
    try {
      return JSON.parse(dataStr);
    } catch {
      return {};
    }
  };

  const getSubmitterName = (data) => {
    if (data.name) return data.name;
    if (data.first_name && data.last_name) return `${data.first_name} ${data.last_name}`;
    if (data.first_name) return data.first_name;
    return null;
  };

  const getSiteUrl = (project) => {
    if (!project) return null;
    if (project.custom_domain) return `https://${project.custom_domain}`;
    if (project.subdomain) return getSubdomainUrl(project.subdomain);
    return null;
  };

  const newCount = submissions.filter((s) => s.status === "new").length;

  return (
    <section className="sub-page">
      <div className="container">
        {/* Header */}
        <div className="sub-header">
          <div className="sub-header-left">
            <h2>Form Submissions</h2>
            <p>
              {submissions.length} total
              {newCount > 0 && (
                <span className="sub-new-count">{newCount} new</span>
              )}
            </p>
          </div>
          <div className="sub-filters">
            <select
              className="sub-filter-select"
              value={filter.project_id}
              onChange={(e) => handleFilterChange("project_id", e.target.value)}
            >
              <option value="">All Projects</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <select
              className="sub-filter-select"
              value={filter.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
            >
              <option value="">All Status</option>
              <option value="new">New</option>
              <option value="read">Read</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        {/* Content */}
        {submissions.length === 0 ? (
          <div className="sub-empty">
            <div className="sub-empty-icon">
              <i className="bi bi-inbox"></i>
            </div>
            <h5>No submissions yet</h5>
            <p>Form submissions from your published sites will appear here.</p>
          </div>
        ) : (
          <div className="sub-table-card">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Project</th>
                  <th>Page</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((sub) => {
                  const data = parseSubmissionData(sub.submission_data);
                  const name = getSubmitterName(data);
                  const siteUrl = getSiteUrl(sub.project);

                  return (
                    <tr
                      key={sub.id}
                      className={sub.status === "new" ? "sub-row-new" : ""}
                    >
                      <td className="sub-date">{formatDate(sub.createdAt)}</td>
                      <td className="sub-email">
                        {sub.email || <span className="sub-muted">—</span>}
                      </td>
                      <td>
                        {name || <span className="sub-muted">—</span>}
                      </td>
                      <td>
                        {siteUrl ? (
                          <a
                            href={siteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sub-project-link"
                          >
                            {sub.project?.name || "—"}
                            <i className="bi bi-box-arrow-up-right"></i>
                          </a>
                        ) : (
                          sub.project?.name || <span className="sub-muted">—</span>
                        )}
                      </td>
                      <td>{sub.page_name || <span className="sub-muted">—</span>}</td>
                      <td>
                        <span className={`sub-status sub-status--${sub.status}`}>
                          {sub.status}
                        </span>
                      </td>
                      <td>
                        <div className="sub-actions">
                          <button
                            className="sub-action-btn"
                            title="View details"
                            onClick={() => handleViewSubmission(sub.id)}
                          >
                            <i className="bi bi-eye"></i>
                          </button>
                          {sub.status === "new" && (
                            <button
                              className="sub-action-btn sub-action-btn--success"
                              title="Mark as read"
                              onClick={() => handleStatusChange(sub.id, "read")}
                            >
                              <i className="bi bi-check2"></i>
                            </button>
                          )}
                          {sub.status !== "archived" && (
                            <button
                              className="sub-action-btn"
                              title="Archive"
                              onClick={() => handleStatusChange(sub.id, "archived")}
                            >
                              <i className="bi bi-archive"></i>
                            </button>
                          )}
                          {sub.status === "archived" && (
                            <button
                              className="sub-action-btn"
                              title="Unarchive"
                              onClick={() => handleStatusChange(sub.id, "read")}
                            >
                              <i className="bi bi-arrow-counterclockwise"></i>
                            </button>
                          )}
                          <button
                            className="sub-action-btn sub-action-btn--danger"
                            title="Delete"
                            onClick={() => handleDelete(sub.id)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showDetail && selectedId && (
        <SubmissionDetail
          submissionId={selectedId}
          onClose={() => {
            setShowDetail(false);
            setSelectedId(null);
          }}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
        />
      )}
    </section>
  );
};

export default SubmissionsList;
